import React, { Fragment, useEffect, useState } from 'react';
import { NodeCheckIcon } from './chromeIcons';
import {
  SECTION_PROGRESS_MS,
  type SectionProgressState,
} from './sectionProgress';

/**
 * Узлы прогресса по разделам (пак `chrome.progress: 'section-nodes'`) —
 * разметка эталона Slimkit (spec shared/header.md «Геометрия», «Анимации»):
 * узел 17×17, дорожка 3 px делит свободное место поровну, заливка дорожки —
 * градиент прогресса темы (`--gradient-progress`, пачка настроек 1) и едет
 * `width .3s ease-in-out`. Галочка на узле появляется через 300 мс после
 * прихода на экран (мгновенно, без анимации), снимается сразу.
 *
 * Плавность заливки видна, только когда шапка живёт вне анимируемого корня
 * экрана (`chrome.persistent`): иначе компонент рождается заново на каждом
 * экране сразу с новой шириной.
 */
export function SectionNodesProgress({
  state,
}: {
  state: SectionProgressState;
}) {
  const shown = useDelayedChecks(state.nodes);
  return (
    <div
      data-testid="quiz-section-progress"
      style={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        height: 24,
      }}
    >
      {shown.map((done, k) => (
        <Fragment key={k}>
          <span
            data-testid={`quiz-section-node-${k}`}
            data-state={done ? 'done' : 'idle'}
            style={{
              width: 17,
              height: 17,
              minWidth: 17,
              borderRadius: '50%',
              flexShrink: 0,
              background: done
                ? 'var(--gradient-progress, var(--color-primary, #1677ff))'
                : 'var(--color-border-primary, #e3e9fb)',
            }}
          >
            {done ? <NodeCheckIcon /> : null}
          </span>
          {k < state.fills.length ? (
            <span
              data-testid={`quiz-section-track-${k}`}
              style={{
                flex: 1,
                height: 3,
                minWidth: 0,
                overflow: 'hidden',
                background: 'var(--color-border-primary, #e3e9fb)',
              }}
            >
              <span
                data-testid={`quiz-section-fill-${k}`}
                style={{
                  display: 'block',
                  height: '100%',
                  width: `${Math.round(state.fills[k] * 10000) / 100}%`,
                  background:
                    'var(--gradient-progress, var(--color-primary, #1677ff))',
                  transition: `width ${SECTION_PROGRESS_MS}ms ease-in-out`,
                }}
              />
            </span>
          ) : null}
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Галочки с задержкой: новые отметки — через SECTION_PROGRESS_MS, снятые —
 * сразу. Первый показ — без задержки (иначе на первом экране узел мигал бы).
 */
export function useDelayedChecks(nodes: readonly boolean[]): boolean[] {
  const [shown, setShown] = useState<boolean[]>(() => [...nodes]);
  const key = nodes.map((n) => (n ? '1' : '0')).join('');

  useEffect(() => {
    // Снятия и смена числа узлов — сразу; добавления — по таймеру.
    setShown((prev) =>
      nodes.map((n, i) =>
        n ? prev.length === nodes.length && prev[i] === true : false,
      ),
    );
    if (!nodes.some(Boolean)) return undefined;
    const timer = setTimeout(() => setShown([...nodes]), SECTION_PROGRESS_MS);
    return () => clearTimeout(timer);
    // `key` — содержимое массива; сам массив новый на каждый рендер.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Длина могла смениться (ветвление добавило раздел) — до эффекта рисуем по
  // новой длине, не выходя за массив.
  return nodes.map((_, i) => shown[i] === true && nodes[i]);
}
